import { AfterViewInit, Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { AcademicsStateService } from '../services/academics-state.service';
import { CommitteesStateService } from '../services/committees-state.service';
import { StudentsActivityPortalStateService } from '../services/students-activity-portal-state.service';

declare var bootstrap: any;

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, AfterViewInit {

  isSticky = false;
  isMobile = window.innerWidth < 992;
  academicsActiveSection$: Observable<string | null>;
  committeesActiveSection$: Observable<string | null>;
  studentsActivityPortalActiveSection$: Observable<string | null>;

  private hoverCloseTimers = new Map<HTMLElement, number>();

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private academicsState: AcademicsStateService,
    private committeesState: CommitteesStateService,
    private studentsActivityPortalState: StudentsActivityPortalStateService,
    private router: Router
  ) {
    // The academics section highlight in this dropdown should only apply
    // while actually on /academics — AcademicsStateService is a root
    // singleton whose value otherwise stays stale on every other page.
    const onAcademicsPage$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects.startsWith('/academics')),
      startWith(this.router.url.startsWith('/academics'))
    );

    this.academicsActiveSection$ = combineLatest([onAcademicsPage$, this.academicsState.activeSection$]).pipe(
      map(([onAcademicsPage, section]) => onAcademicsPage ? section : null)
    );

    // Same reasoning as onAcademicsPage$ above, scoped to /committees.
    const onCommitteesPage$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects.startsWith('/committees')),
      startWith(this.router.url.startsWith('/committees'))
    );

    this.committeesActiveSection$ = combineLatest([onCommitteesPage$, this.committeesState.activeSection$]).pipe(
      map(([onCommitteesPage, section]) => onCommitteesPage ? section : null)
    );

    const onStudentsActivityPortalPage$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects.startsWith('/students-activity-portal')),
      startWith(this.router.url.startsWith('/students-activity-portal'))
    );

    this.studentsActivityPortalActiveSection$ = combineLatest([
      onStudentsActivityPortalPage$,
      this.studentsActivityPortalState.activeSection$
    ]).pipe(
      map(([onStudentsActivityPortalPage, section]) => onStudentsActivityPortalPage ? section : null)
    );
  }

  ngOnInit(): void {
    this.checkScreen();
  }

  ngAfterViewInit(): void {
    const dropdowns = this.elementRef.nativeElement.querySelectorAll<HTMLElement>('.navbar .nav-item.dropdown');

    dropdowns.forEach(item => {
      const toggle = item.querySelector<HTMLElement>('[data-bs-toggle="dropdown"]');

      if (!toggle) {
        return;
      }

      item.addEventListener('mouseenter', () => this.openDropdownOnHover(item, toggle));
      item.addEventListener('mouseleave', () => this.scheduleCloseDropdown(item, toggle));
    });
  }

  private openDropdownOnHover(item: HTMLElement, toggle: HTMLElement): void {
    if (this.isMobile) {
      return;
    }

    const pending = this.hoverCloseTimers.get(item);

    if (pending) {
      window.clearTimeout(pending);
      this.hoverCloseTimers.delete(item);
    }

    if (toggle.getAttribute('aria-expanded') !== 'true') {
      toggle.click();
    }
  }

  private scheduleCloseDropdown(item: HTMLElement, toggle: HTMLElement): void {
    if (this.isMobile) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (toggle.getAttribute('aria-expanded') === 'true') {
        toggle.click();
      }
      this.hoverCloseTimers.delete(item);
    }, 200);

    this.hoverCloseTimers.set(item, timer);
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    this.isSticky = window.pageYOffset > 0;
  }

  @HostListener('window:resize', [])
  onResize(): void {
    this.checkScreen();
  }

  checkScreen(): void {
    this.isMobile = window.innerWidth < 992;
  }

  closeNavbar(): void {

    if (!this.isMobile) {
      return;
    }

    const navbar = document.getElementById('navbar-content');

    if (navbar) {
      const collapse = bootstrap.Collapse.getOrCreateInstance(navbar);
      collapse.hide();
    }

  }

}
